import {
  CORE_FEATURES,
  EXPERIMENTAL_FEATURES,
  POPULAR_FEATURES,
  RISKY_FEATURES,
} from ".";
import { Rule } from "../types";

export const RULES: Rule[] = [
  {
    id: "css-has",
    featureId: "css-selector-has",
    files: ["css", "js", "ts", "tsx"],
    regex: /:has\s*\(/g,
    message: "CSS :has() selector may not be in your baseline.",
    tags: ["css", "popular", "bug-prone"],
    docs: "https://developer.mozilla.org/docs/Web/CSS/:has",
  },
  {
    id: "css-is",
    featureId: "css-selector-is",
    files: ["css", "js", "ts", "tsx"],
    regex: /:is\s*\(/g,
    message: "CSS :is() selector compatibility check.",
    tags: ["css", "popular"],
  },
  {
    id: "css-where",
    featureId: "css-selector-where",
    files: ["css", "js", "ts", "tsx"],
    regex: /:where\s*\(/g,
    message: "CSS :where() selector compatibility check.",
    tags: ["css"],
  },
  {
    id: "css-focus-visible",
    featureId: "css-pseudo-class-focus-visible",
    files: ["css", "js", "ts", "tsx"],
    regex: /:focus-visible\b/g,
    message: "CSS :focus-visible may not be in your baseline.",
    tags: ["css", "popular"],
  },
  {
    id: "css-focus-within",
    featureId: "css-pseudo-class-focus-within",
    files: ["css", "js", "ts", "tsx"],
    regex: /:focus-within\b/g,
    message: "CSS :focus-within compatibility check.",
    tags: ["css"],
  },

  /* =========================
   * CSS Layout / Containers / Nesting
   * ========================= */
  {
    id: "css-container-queries",
    featureId: "css-container-queries",
    files: ["css"],
    regex: /@container\b/g,
    message: "CSS Container Queries may not be in your baseline.",
    tags: ["css", "popular", "bug-prone"],
  },
  {
    id: "css-container-units",
    featureId: "css-container-units",
    files: ["css"],
    regex: /\b(cqw|cqh|cqi|cqb|cqmin|cqmax)\b/g,
    message: "Container query length units may not be in your baseline.",
    tags: ["css", "popular"],
  },
  {
    id: "css-nesting",
    featureId: "css-nesting-rules",
    files: ["css"],
    regex: /(^|\s)&\s*[{:]/gm,
    message: "CSS Nesting may not be in your baseline.",
    tags: ["css", "popular"],
  },
  {
    id: "css-layer",
    featureId: "css-at-rule-layer",
    files: ["css"],
    regex: /@layer\b/g,
    message: "CSS @layer may not be in your baseline.",
    tags: ["css", "popular"],
  },
  {
    id: "css-subgrid",
    featureId: "css-grid-subgrid",
    files: ["css"],
    regex: /\bsubgrid\b/g,
    message: "CSS Subgrid may not be in your baseline.",
    tags: ["css", "bug-prone"],
  },
  {
    id: "css-scope",
    featureId: "css-at-rule-scope",
    files: ["css"],
    regex: /@scope\b/g,
    message: "CSS @scope may not be in your baseline.",
    tags: ["css", "experimental"],
  },

  /* =========================
   * CSS Color / Typography / Visual
   * ========================= */
  {
    id: "css-accent-color",
    featureId: "css-accent-color",
    files: ["css"],
    regex: /\baccent-color\s*:/g,
    message: "CSS accent-color may not be in your baseline.",
    tags: ["css", "popular"],
  },
  {
    id: "css-text-wrap-balance",
    featureId: "css-text-wrap-balance",
    files: ["css"],
    regex: /\btext-wrap\s*:\s*balance\b/g,
    message: "CSS text-wrap: balance may not be in your baseline.",
    tags: ["css", "popular"],
  },
  {
    id: "css-color-function",
    featureId: "css-color-function",
    files: ["css"],
    regex: /\bcolor\(/g,
    message: "CSS color() function may not be in your baseline.",
    tags: ["css", "popular"],
  },
  {
    id: "css-color-mix",
    featureId: "css-color-mix",
    files: ["css"],
    regex: /\bcolor-mix\(/g,
    message: "CSS color-mix() may not be in your baseline.",
    tags: ["css", "popular"],
  },
  {
    id: "css-lab",
    featureId: "css-color-lab-lch",
    files: ["css"],
    regex: /\blab\(/g,
    message: "CSS lab() color may not be in your baseline.",
    tags: ["css", "popular"],
  },
  {
    id: "css-lch",
    featureId: "css-color-lab-lch",
    files: ["css"],
    regex: /\blch\(/g,
    message: "CSS lch() color may not be in your baseline.",
    tags: ["css", "popular"],
  },
  {
    id: "css-oklab",
    featureId: "css-color-oklab-oklch",
    files: ["css"],
    regex: /\boklab\(/g,
    message: "CSS oklab() color may not be in your baseline.",
    tags: ["css"],
  },
  {
    id: "css-oklch",
    featureId: "css-color-oklab-oklch",
    files: ["css"],
    regex: /\boklch\(/g,
    message: "CSS oklch() color may not be in your baseline.",
    tags: ["css"],
  },
  {
    id: "css-backdrop-filter",
    featureId: "css-backdrop-filter",
    files: ["css"],
    regex: /\bbackdrop-filter\s*:/g,
    message: "CSS backdrop-filter may not be in your baseline.",
    tags: ["css", "bug-prone"],
  },
  {
    id: "css-scrollbar-gutter",
    featureId: "css-scrollbar-gutter",
    files: ["css"],
    regex: /\bscrollbar-gutter\s*:/g,
    message: "CSS scrollbar-gutter may not be in your baseline.",
    tags: ["css"],
  },

  /* =========================
   * CSS Scroll/Timeline/View Transitions/Anchor
   * ========================= */
  {
    id: "css-scroll-timeline",
    featureId: "api-scroll-timeline",
    files: ["css"],
    regex: /@scroll-timeline\b/g,
    message:
      "Scroll-driven animations (scroll-timeline) may not be in your baseline.",
    tags: ["css", "popular", "bug-prone"],
  },
  {
    id: "css-animation-timeline",
    featureId: "css-animation-timeline",
    files: ["css"],
    regex: /\banimation-timeline\s*:/g,
    message: "CSS animation-timeline may not be in your baseline.",
    tags: ["css"],
  },
  {
    id: "css-view-timeline",
    featureId: "css-view-timeline",
    files: ["css"],
    regex: /\bview-timeline[-\w]*\s*:/g,
    message: "CSS view-timeline* properties may not be in your baseline.",
    tags: ["css"],
  },
  {
    id: "css-view-transitions",
    featureId: "css-view-transitions",
    files: ["css"],
    regex: /\bview-transition-name\s*:/g,
    message: "CSS View Transitions properties may not be in your baseline.",
    tags: ["css", "popular"],
  },
  {
    id: "css-anchor-positioning",
    featureId: "css-anchor-positioning",
    files: ["css"],
    regex: /\banchor\(/g,
    message: "CSS Anchor Positioning may not be in your baseline.",
    tags: ["css", "experimental", "bug-prone"],
  },
  {
    id: "css-properties-and-values",
    featureId: "css-properties-and-values",
    files: ["css"],
    regex: /@property\s+--/g,
    message:
      "CSS Properties & Values API (@property) may not be in your baseline.",
    tags: ["css", "experimental"],
  },
  {
    id: "css-media-range-syntax",
    featureId: "css-media-range-syntax",
    files: ["css"],
    // crude detection of >=, <=, <, > inside a media query
    regex: /@media[^{]*\(\s*[^)<>]*[<>]=?\s*[^)]+\)/g,
    message: "CSS Media Queries Range Syntax may not be in your baseline.",
    tags: ["css"],
  },

  /* =========================
   * HTML / Attributes / Elements
   * ========================= */
  {
    id: "html-dialog",
    featureId: "html-dialog-element",
    files: ["html", "js", "ts", "tsx"],
    regex: /<dialog\b|\.showModal\s*\(/g,
    message: "<dialog> element or dialog API may not be safe.",
    tags: ["html", "popular", "bug-prone"],
  },
  {
    id: "html-popover",
    featureId: "html-popover-api",
    files: ["html", "js", "ts", "tsx"],
    regex:
      /\bpopover\s*=\s*['"][^'"]*['"]|\.(showPopover|hidePopover|togglePopover)\s*\(/g,
    message: "HTML Popover API may not be in your baseline.",
    tags: ["html", "popular", "bug-prone"],
  },
  {
    id: "html-inert",
    featureId: "html-inert-attribute",
    files: ["html", "js", "ts", "tsx"],
    regex: /<[^>]+\s(inert)(\s|>)/g,
    message: "HTML inert attribute may not be in your baseline.",
    tags: ["html", "popular"],
  },
  {
    id: "html-img-loading",
    featureId: "html-img-loading-attribute",
    files: ["html"],
    regex: /\bloading\s*=\s*['"](lazy|eager)['"]/g,
    message: "img/iframe loading attribute may not be in your baseline.",
    tags: ["html", "popular"],
  },
  {
    id: "html-fetchpriority",
    featureId: "html-fetchpriority-attribute",
    files: ["html"],
    regex: /\bfetchpriority\s*=\s*['"](high|low|auto)['"]/g,
    message: "fetchpriority attribute may not be in your baseline.",
    tags: ["html"],
  },
  {
    id: "html-decoding",
    featureId: "html-img-decoding-attribute",
    files: ["html"],
    regex: /\bdecoding\s*=\s*['"](sync|async|auto)['"]/g,
    message: "img decoding attribute may not be in your baseline.",
    tags: ["html"],
  },
  {
    id: "html-enterkeyhint",
    featureId: "html-enterkeyhint-attribute",
    files: ["html"],
    regex: /\benterkeyhint\s*=\s*['"][^'"]+['"]/g,
    message: "enterkeyhint attribute may not be in your baseline.",
    tags: ["html"],
  },
  {
    id: "html-autocapitalize",
    featureId: "html-autocapitalize-attribute",
    files: ["html"],
    regex: /\bautocapitalize\s*=\s*['"][^'"]+['"]/g,
    message: "autocapitalize attribute may not be in your baseline.",
    tags: ["html"],
  },

  /* =========================
   * JavaScript / RegExp / Language
   * ========================= */
  {
    id: "js-regexp-lookbehind",
    featureId: "js-regexp-lookbehind",
    files: ["js", "ts", "tsx"],
    regex: /\(\?<=|\(\?<!/g,
    message: "RegExp lookbehind may not be supported by your targets.",
    tags: ["js", "popular", "bug-prone"],
    docs: "https://developer.mozilla.org/docs/Web/JavaScript/Reference/Regular_expressions/Lookbehind_assertion",
  },
  {
    id: "js-regexp-named-groups",
    featureId: "js-regexp-named-capture-groups",
    files: ["js", "ts", "tsx"],
    regex: /\(\?<[_$a-zA-Z][$\w]*/g,
    message: "RegExp named capture groups may not be in your baseline.",
    tags: ["js"],
  },

  /* =========================
   * Web APIs (DX & App features)
   * ========================= */
  {
    id: "api-view-transitions",
    featureId: "api-view-transitions",
    files: ["js", "ts", "tsx"],
    regex: /\bdocument\.startViewTransition\s*\(/g,
    message: "View Transitions API may not be in your baseline.",
    tags: ["js", "popular", "bug-prone"],
  },
  {
    id: "async-clipboard",
    featureId: "async-clipboard",
    files: ["js", "ts", "tsx"],
    regex: /navigator\.clipboard\.(writeText|readText|write|read)\b/g,
    message: "Async Clipboard API may not be in your baseline.",
    tags: ["js", "popular"],
  },
  {
    id: "clipboarditem",
    featureId: "async-clipboard",
    files: ["js", "ts", "tsx"],
    regex: /\bnew\s+ClipboardItem\s*\(/g,
    message: "ClipboardItem (Async Clipboard) may not be in your baseline.",
    tags: ["js"],
  },
  {
    id: "file-system-access",
    featureId: "file-system-access",
    files: ["js", "ts", "tsx"],
    regex: /\bshow(Open|Save|Directory)FilePicker\s*\(/g,
    message: "File System Access API may not be in your baseline.",
    tags: ["js", "popular", "bug-prone"],
  },
  {
    id: "web-share",
    featureId: "web-share",
    files: ["js", "ts", "tsx"],
    regex: /navigator\.share\s*\(/g,
    message: "Web Share API may not be in your baseline.",
    tags: ["js", "popular"],
  },
  {
    id: "web-share-can-share",
    featureId: "web-share",
    files: ["js", "ts", "tsx"],
    regex: /navigator\.canShare\s*\(/g,
    message: "Web Share (canShare) may not be in your baseline.",
    tags: ["js"],
  },
  {
    id: "permissions-api",
    featureId: "permissions-api",
    files: ["js", "ts", "tsx"],
    regex: /navigator\.permissions\.query\s*\(/g,
    message: "Permissions API may not be in your baseline.",
    tags: ["js"],
  },
  {
    id: "eyedropper-api",
    featureId: "eyedropper-api",
    files: ["js", "ts", "tsx"],
    regex: /\bnew\s+EyeDropper\s*\(/g,
    message: "EyeDropper API may not be in your baseline.",
    tags: ["js", "popular"],
  },
  {
    id: "contact-picker",
    featureId: "contact-picker",
    files: ["js", "ts", "tsx"],
    regex: /navigator\.contacts\.select\s*\(/g,
    message: "Contact Picker API may not be in your baseline.",
    tags: ["js", "experimental"],
  },
  {
    id: "credential-management",
    featureId: "credential-management",
    files: ["js", "ts", "tsx"],
    regex: /navigator\.credentials\.(get|create)\s*\(/g,
    message: "Credential Management API may not be in your baseline.",
    tags: ["js"],
  },
  {
    id: "webauthn",
    featureId: "webauthn",
    files: ["js", "ts", "tsx"],
    regex: /\bPublicKeyCredential\b/g,
    message: "WebAuthn may not be in your baseline.",
    tags: ["js", "popular", "bug-prone"],
  },
  {
    id: "payment-request",
    featureId: "payment-request",
    files: ["js", "ts", "tsx"],
    regex: /\bnew\s+PaymentRequest\s*\(/g,
    message: "Payment Request API may not be in your baseline.",
    tags: ["js"],
  },
  {
    id: "badging-api",
    featureId: "badging-api",
    files: ["js", "ts", "tsx"],
    regex: /navigator\.(setAppBadge|clearAppBadge)\s*\(/g,
    message: "Badging API may not be in your baseline.",
    tags: ["js"],
  },
  {
    id: "screen-wake-lock",
    featureId: "screen-wake-lock",
    files: ["js", "ts", "tsx"],
    regex: /navigator\.wakeLock\.request\s*\(/g,
    message: "Screen Wake Lock API may not be in your baseline.",
    tags: ["js"],
  },
  {
    id: "screen-orientation",
    featureId: "screen-orientation",
    files: ["js", "ts", "tsx"],
    regex: /screen\.orientation\.(lock|unlock)\s*\(/g,
    message: "Screen Orientation API may not be in your baseline.",
    tags: ["js"],
  },
  {
    id: "media-session",
    featureId: "media-session",
    files: ["js", "ts", "tsx"],
    regex: /navigator\.mediaSession\b/g,
    message: "Media Session API may not be in your baseline.",
    tags: ["js"],
  },
  {
    id: "document-pip",
    featureId: "document-picture-in-picture",
    files: ["js", "ts", "tsx"],
    regex: /\bdocumentPictureInPicture\b/g,
    message: "Document Picture-in-Picture may not be in your baseline.",
    tags: ["js", "bug-prone"],
  },
  {
    id: "video-pip",
    featureId: "api-picture-in-picture",
    files: ["js", "ts", "tsx"],
    regex: /\.requestPictureInPicture\s*\(/g,
    message: "HTMLVideoElement Picture-in-Picture may not be in your baseline.",
    tags: ["js"],
  },
  {
    id: "offscreencanvas",
    featureId: "offscreencanvas",
    files: ["js", "ts", "tsx"],
    regex: /\bnew\s+OffscreenCanvas\s*\(/g,
    message: "OffscreenCanvas may not be in your baseline.",
    tags: ["js"],
  },
  {
    id: "module-workers",
    featureId: "module-workers",
    files: ["js", "ts", "tsx"],
    regex: /new\s+Worker\([^,]+,\s*\{\s*type\s*:\s*['"]module['"]\s*\}\s*\)/g,
    message: "Module workers may not be in your baseline.",
    tags: ["js"],
  },
  {
    id: "urlpattern",
    featureId: "urlpattern",
    files: ["js", "ts", "tsx"],
    regex: /\bnew\s+URLPattern\s*\(/g,
    message: "URLPattern may not be in your baseline.",
    tags: ["js", "popular"],
  },
  {
    id: "intl-segmenter",
    featureId: "intl-segmenter",
    files: ["js", "ts", "tsx"],
    regex: /\bIntl\.Segmenter\b/g,
    message: "Intl.Segmenter may not be in your baseline.",
    tags: ["js"],
  },
  {
    id: "scheduler-post-task",
    featureId: "scheduler-post-task",
    files: ["js", "ts", "tsx"],
    regex: /\bscheduler\.postTask\s*\(/g,
    message: "scheduler.postTask may not be in your baseline.",
    tags: ["js", "experimental"],
  },
  {
    id: "navigation-api",
    featureId: "navigation-api",
    files: ["js", "ts", "tsx"],
    regex: /\bnavigation\.(navigate|addEventListener|currentEntry)\b/g,
    message: "Navigation API may not be in your baseline.",
    tags: ["js", "experimental", "bug-prone"],
  },
  {
    id: "url-can-parse",
    featureId: "url-canparse",
    files: ["js", "ts", "tsx"],
    regex: /\bURL\.canParse\s*\(/g,
    message: "URL.canParse may not be in your baseline.",
    tags: ["js"],
  },
  {
    id: "compression-streams",
    featureId: "compression-streams",
    files: ["js", "ts", "tsx"],
    regex: /\bnew\s+(CompressionStream|DecompressionStream)\s*\(/g,
    message: "Compression Streams may not be in your baseline.",
    tags: ["js"],
  },
  {
    id: "transformstream",
    featureId: "streams",
    files: ["js", "ts", "tsx"],
    regex: /\bnew\s+TransformStream\s*\(/g,
    message: "TransformStream may not be in your baseline.",
    tags: ["js"],
  },
  {
    id: "broadcast-channel",
    featureId: "broadcast-channel",
    files: ["js", "ts", "tsx"],
    regex: /\bnew\s+BroadcastChannel\s*\(/g,
    message: "BroadcastChannel may not be in your baseline.",
    tags: ["js"],
  },

  /* =========================
   * Hardware / Powerful Features (Risky)
   * ========================= */
  {
    id: "webgpu",
    featureId: "webgpu",
    files: ["js", "ts", "tsx"],
    regex: /\bnavigator\.gpu\b/g,
    message: "WebGPU may not be in your baseline.",
    tags: ["js", "risky", "bug-prone"],
  },
  {
    id: "webusb",
    featureId: "web-usb",
    files: ["js", "ts", "tsx"],
    regex: /\bnavigator\.usb\b/g,
    message: "WebUSB may not be in your baseline.",
    tags: ["js", "risky"],
  },
  {
    id: "webbluetooth",
    featureId: "web-bluetooth",
    files: ["js", "ts", "tsx"],
    regex: /\bnavigator\.bluetooth\b/g,
    message: "Web Bluetooth may not be in your baseline.",
    tags: ["js", "risky"],
  },
  {
    id: "webserial",
    featureId: "web-serial",
    files: ["js", "ts", "tsx"],
    regex: /\bnavigator\.serial\b/g,
    message: "Web Serial may not be in your baseline.",
    tags: ["js", "risky"],
  },
  {
    id: "web-locks",
    featureId: "web-locks",
    files: ["js", "ts", "tsx"],
    regex: /\bnavigator\.locks\b/g,
    message: "Web Locks API may not be in your baseline.",
    tags: ["js"],
  },
  {
    id: "shared-array-buffer",
    featureId: "shared-array-buffer",
    files: ["js", "ts", "tsx"],
    regex: /\bSharedArrayBuffer\b/g,
    message:
      "SharedArrayBuffer requires cross-origin isolation; may not be safe in all targets.",
    tags: ["js", "risky"],
  },
  {
    id: "abort-signal-any",
    featureId: "abort-signal-any",
    files: ["js", "ts", "tsx"],
    regex: /\bAbortSignal\.any\s*\(/g,
    message: "AbortSignal.any may not be in your baseline.",
    tags: ["js", "experimental"],
  },
  {
    id: "getdisplaymedia",
    featureId: "screen-capture",
    files: ["js", "ts", "tsx"],
    regex: /navigator\.mediaDevices\.getDisplayMedia\s*\(/g,
    message: "Screen Capture (getDisplayMedia) may not be in your baseline.",
    tags: ["js", "popular"],
  },
];

export const PACKS: Record<string, string[]> = {
  core: CORE_FEATURES,
  popular: POPULAR_FEATURES,
  risky: RISKY_FEATURES,
  experimental: EXPERIMENTAL_FEATURES,
  all: RULES.map((r) => r.id),
};
